import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { SectionContent, getContentResponse } from '../../Services/UserProjectService';
import { useContentVariation, ProjectTreatment } from '../../hooks/useContentVariation';

interface QuestionMultipleViewerProps {
  content: SectionContent;
  project?: ProjectTreatment;
  onResponseChange?: (isValid: boolean, response: string) => void;
  navigationDirection?: 'forward' | 'backward';
}

const QuestionMultipleViewer: React.FC<QuestionMultipleViewerProps> = ({ content, project, onResponseChange, navigationDirection = 'forward' }) => {
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [freeTextAnswers, setFreeTextAnswers] = useState<{[key: number]: string}>({});
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const { getVariationText } = useContentVariation(project);

  // Get question text using variations
  const questionContent = {
    content: (content as any).content?.questionText || (content as any).content || content
  };
  const questionText = getVariationText(questionContent);
  const allowMultiple = (content as any).content?.allowMultiple || false;
  const answers = (content as any).content?.answers || [];

  // Load saved response if content is completed
  useEffect(() => {
    const loadSavedResponse = async () => {
      if (!content.isCompleted) return;

      // For USER, the content.id IS the projectSectionContentId
      const projectSectionContentId = content.id;

      if (!projectSectionContentId) {
        console.warn('No content ID found for USER content:', content);
        return;
      }

      setIsLoadingResponse(true);
      try {
        const savedResponse = await getContentResponse(projectSectionContentId);
        if (savedResponse && savedResponse.responseContent) {
          if (allowMultiple) {
            // Multiple choice: parse JSON response
            try {
              const responseData = JSON.parse(savedResponse.responseContent);
              if (responseData.selectedAnswers) {
                setSelectedAnswers(responseData.selectedAnswers);
              }
              if (responseData.freeTextAnswers) {
                setFreeTextAnswers(responseData.freeTextAnswers);
              }
            } catch (error) {
              console.error('Error parsing multiple choice response:', error);
            }
          } else {
            // Single choice: handle different formats
            const responseContent = savedResponse.responseContent;

            try {
              // Try to parse as JSON (old formats)
              const responseData = JSON.parse(responseContent);

              if (responseData.selectedOptionText) {
                // Old JSON format with selectedOptionText
                const matchingIndex = answers.findIndex((answer: any) => {
                  const answerText = getVariationText(answer.text) || `Opción ${answers.indexOf(answer) + 1}`;
                  return answerText === responseData.selectedOptionText;
                });
                if (matchingIndex !== -1) {
                  setSelectedAnswers([matchingIndex]);
                }
                if (responseData.freeTextAnswers) {
                  setFreeTextAnswers(responseData.freeTextAnswers);
                }
              } else if (responseData.selectedAnswers && responseData.selectedAnswers.length > 0) {
                // Backward compatibility: old format with indices
                setSelectedAnswers(responseData.selectedAnswers);
                if (responseData.freeTextAnswers) {
                  setFreeTextAnswers(responseData.freeTextAnswers);
                }
              }
            } catch (error) {
              // New format: plain text, need to parse combined text
              const combinedText = responseContent.trim();

              // Try to find matching option and extract free text
              let matchingIndex = -1;
              let extractedFreeText = '';

              for (let i = 0; i < answers.length; i++) {
                const answer = answers[i];
                const answerText = getVariationText(answer.text) || `Opción ${i + 1}`;

                if (combinedText === answerText) {
                  // Exact match without free text
                  matchingIndex = i;
                  break;
                } else if (combinedText.startsWith(`${answerText}: `)) {
                  // Match with free text
                  matchingIndex = i;
                  extractedFreeText = combinedText.substring(`${answerText}: `.length);
                  break;
                }
              }

              if (matchingIndex !== -1) {
                setSelectedAnswers([matchingIndex]);
                if (extractedFreeText) {
                  setFreeTextAnswers({ [matchingIndex]: extractedFreeText });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading saved response:', error);
      } finally {
        setIsLoadingResponse(false);
      }
    };

    loadSavedResponse();
  }, [content]);

  // Notify parent component when response changes
  useEffect(() => {
    const hasSelectedAnswers = selectedAnswers.length > 0;

    // Free text is optional - only require at least one answer to be selected
    const isValid = hasSelectedAnswers;

    // Format response for API
    let responseData;

    if (allowMultiple) {
      // Multiple choice: keep current format with indices
      responseData = {
        selectedAnswers,
        freeTextAnswers: Object.fromEntries(
          Object.entries(freeTextAnswers).filter(([_, text]) => text.trim())
        ),
        allowMultiple
      };
    } else {
      // Single choice: save combined text (option text + free text if any)
      const selectedIndex = selectedAnswers[0];
      const selectedOptionText = selectedIndex !== undefined && answers[selectedIndex]
        ? getVariationText(answers[selectedIndex].text) || `Opción ${selectedIndex + 1}`
        : '';

      // Check if there's free text for the selected option
      const selectedOptionFreeText = selectedIndex !== undefined && freeTextAnswers[selectedIndex]
        ? freeTextAnswers[selectedIndex].trim()
        : '';

      // Combine option text with free text if available
      const combinedText = selectedOptionFreeText
        ? `${selectedOptionText}: ${selectedOptionFreeText}`
        : selectedOptionText;

      // For single choice, send the combined text directly (not JSON)
      onResponseChange?.(isValid, combinedText);
      return;
    }

    onResponseChange?.(isValid, JSON.stringify(responseData));
  }, [selectedAnswers, freeTextAnswers, answers, onResponseChange, allowMultiple]);

  const handleOptionToggle = (optionIndex: number) => {
    if (allowMultiple) {
      // Checkbox behavior
      setSelectedAnswers(prev => 
        prev.includes(optionIndex)
          ? prev.filter(i => i !== optionIndex)
          : [...prev, optionIndex]
      );
    } else {
      // Radio behavior
      setSelectedAnswers([optionIndex]);
    }
  };

  const handleFreeTextChange = (optionIndex: number, text: string) => {
    setFreeTextAnswers(prev => ({
      ...prev,
      [optionIndex]: text
    }));
  };

  return (
    <motion.div
      initial={{ x: navigationDirection === 'forward' ? 300 : -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: navigationDirection === 'forward' ? -300 : 300, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="question-multiple-content"
      style={{
        maxWidth: '100%',
        width: '100%',
        padding: '0 2rem'
      }}
    >
      <div className="question-text mb-4" style={{ textAlign: 'left' }}>
        <p style={{ 
          fontSize: '1.1rem', // Fixed size for all questions
          lineHeight: '2.2',
          margin: '0',
          fontWeight: '300'
        }}
        dangerouslySetInnerHTML={{ __html: questionText }}
        />
      </div>

      {isLoadingResponse && (
        <div className="text-center mb-3" style={{ color: '#6c757d' }}>
          <small>Cargando respuesta guardada...</small>
        </div>
      )}

      <div className="options-section">
        {answers.map((answer: any, index: number) => {
          const isSelected = selectedAnswers.includes(index);
          const hasFreeText = answer.isFreeText === true;
          
          return (
            <div key={index} className="option-item mb-3">
              <div className="form-check" style={{ display: 'flex', alignItems: 'flex-start' }}>
                {allowMultiple ? (
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`answer-${index}`}
                    checked={isSelected}
                    onChange={() => handleOptionToggle(index)}
                    disabled={isLoadingResponse}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      marginTop: '4px',
                      flexShrink: 0,
                      cursor: isLoadingResponse ? 'not-allowed' : 'pointer'
                    }}
                  />
                ) : (
                  <input
                    className="form-check-input"
                    type="radio"
                    name="question-options"
                    id={`answer-${index}`}
                    checked={isSelected}
                    onChange={() => handleOptionToggle(index)}
                    disabled={isLoadingResponse}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      marginTop: '4px',
                      flexShrink: 0,
                      cursor: isLoadingResponse ? 'not-allowed' : 'pointer'
                    }}
                  />
                )}
                <label
                  className="form-check-label"
                  htmlFor={`answer-${index}`}
                  style={{
                    fontSize: '1.1rem',
                    lineHeight: '1.8',
                    cursor: 'pointer',
                    flex: 1
                  }}
                  dangerouslySetInnerHTML={{ __html: getVariationText(answer.text) || `Opción ${index + 1}` }}
                />
              </div>
              {hasFreeText && (
                <div className="mt-2 ms-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Especifica tu respuesta..."
                    value={freeTextAnswers[index] || ''}
                    onChange={(e) => handleFreeTextChange(index, e.target.value)}
                    disabled={!isSelected || isLoadingResponse}
                    style={{
                      fontSize: '1rem',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#fff' : '#f8f9fa',
                      opacity: isSelected ? 1 : 0.7
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default QuestionMultipleViewer;